import { registerSW } from 'virtual:pwa-register';

let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null;
let updateReady = false;

// Registers the service worker that precaches the app shell. The SW is a
// progressive enhancement: offline media lives in IndexedDB and does not depend
// on it. We expose updateReady so the player can decide *when* to apply a new
// build (respecting config.update_preference) instead of reloading mid-playback.
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateReady = true;
    },
    onOfflineReady() {
      // App shell cached; ready to run offline.
    },
  });
}

export function isUpdateReady(): boolean {
  return updateReady;
}

// Ask the browser to re-fetch sw.js and install a newer build if one exists.
// This only moves a new SW into the "waiting" state (after which onNeedRefresh
// fires and updateReady flips to true); it never reloads the page. Safe to call
// on a timer or in response to a server push. A long-running kiosk almost never
// navigates, so the browser rarely checks on its own - we drive it explicitly.
export async function checkForUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    await reg?.update();
  } catch {
    // Offline or transient network error; the next scheduled check will retry.
  }
}

// Apply a pending SW update and reload. Called by the player at a safe moment.
// In 'prompt' mode this tells the waiting SW to skipWaiting; the workbox-window
// 'controlling' listener then reloads the page once the new SW takes over.
export async function applyPendingUpdate(): Promise<void> {
  if (applyUpdate && updateReady) {
    await applyUpdate(true);
  }
}

// Force the latest build to apply *now*, bypassing the update_preference window.
// If a build is already waiting it activates immediately; otherwise we fetch one
// and poll briefly for it to install before applying. Activation reloads the
// page. Returns false if no update became available within the timeout.
// Used by the manual settings button and the `app.force_update` socket event.
export async function forceUpdate(timeoutMs = 12_000): Promise<boolean> {
  if (isUpdateReady()) {
    await applyPendingUpdate();
    return true;
  }
  await checkForUpdate();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (isUpdateReady()) {
      await applyPendingUpdate();
      return true;
    }
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}
